import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, Component, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { DOCUMENT, APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { CognitoAuth } from 'amazon-cognito-auth-js';
import { timer, throwError, BehaviorSubject, ReplaySubject, Observable, of, from } from 'rxjs';
import { concatMap, retryWhen, catchError, mergeMap, finalize, filter, take, switchMap, map } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpParams, HttpHeaders, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import * as i2 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import * as i1$1 from 'ngx-toastr';
import { ToastrModule } from 'ngx-toastr';
import * as i1$2 from '@angular/router';

// The Cognito Config
const COGNITO_CONFIG = new InjectionToken('cognito auth options');
// Array of Cognito Config Options for High Availability
const COGNITO_CONFIG_HA = new InjectionToken('cognito high availability auth options');
// Name of the application used to read the list of groups from auth API
const APPLICATION_NAME = new InjectionToken('Application Name');
// API Gateway URL and STAGE to be used by abstract-rest.service class
const APIGATEWAY_CONFIG = new InjectionToken('API Gateway config');
// True if screenauth is allowed for this app, defaults to false
const ALLOW_SCREENAUTH = new InjectionToken('AllowScreenAuth Flag');

/**
 * All API REST calls should follow this format in returns for consistency across applications
 *
 * Bdoy<T>: Optionally allows setting a type to define what is expected from the API REST call.
 * ErrorMessage: Typically blank if status code was 200, if not then will contain information about the error
 * Timestamp: The timestamp the result was generate
 */
class GenericLambdaReturn {
}
/**
 * Provides all the functionality needed for making GET/POST/PUT/DELETE API calls. The APIGATEWAY_CONFIG is injected from the application to provide a default
 * url and stage.
 */
class AbstractRestService {
    //private httpAPIKey:HttpClient; // Client for API calls that will by pass HTTP Interceptor
    /**
     * Constructor
     *
     * @param http An HttpClient that will be used by the requests for making calls
     * @param apiGatewayConfig Injected APIGATEWAY_CONFIG from the environment file of the application
     */
    constructor(http, 
    //private handler: HttpBackend,
    apiGatewayConfig) {
        this.http = http;
        this.apiGatewayConfig = apiGatewayConfig;
        this.refreshTime = 30000; //default refresh time
        this.retryExcludeCodes = [400]; // HTTP Status codes to not retry
        //this.httpAPIKey = new HttpClient(this.handler); // Setup with HttpBackend handler to by pass HTTP Interceptor which will error for API Key calls
        this.postInit();
    }
    /**
     * Make a REST GET Request, minimum needs an ApiResource passed and will typically also include params.
     *
     * GET requests is typically used to retrieve existing records.
     *
     * By default if you don't include repeatCall in request parameter, this will set repeatCall to true and repeat the call automatically every 30 seconds
     *
     * @param request AbstractRestParms interface, minimum required is ApiResource, typically will also include params
     *
     * @returns Observable, must subscribe in order for the call to process
     */
    get(request) {
        let httpObservable$;
        // Default to repeat if not passed explicitly
        if (request.repeatCall === undefined) {
            request.repeatCall = true;
        }
        let p = this.getHttpParams(request);
        let headers = this.getHttpHeaders(request);
        //httpObservable$ = this.httpAPIKey.get<T>(this.getURL(request), {params: p, headers: headers});
        httpObservable$ = this.http.get(this.getURL(request), { params: p, headers: headers });
        return this.finalizeRequest(request, httpObservable$); // Handles Repeat and wraps error handling
    }
    /**
     * Make a REST GET Download Request. This should only be used for downloading binary files from API endpoints that return a valid BLOB.
     *
     * Minimum needs an ApiResource passed and will typically also include params.
     *
     * By default, if you don't pass repeatCall in request parameter, this will set repeatCall to false. This is different than the standard get(). This
     * is done because typically downloading files should not repeat automatically.
     *
     * If there is an error, it will be processed as JSON and returned in err as such for processing by calling application.
     *
     * @param request AbstractRestParms interface, minimum required is ApiResource, typically will also include params
     *
     * @returns Observable, must subscribe in order for the call to process
     */
    getBinary(request) {
        let httpObservable$;
        // Default to repeat if not passed explicitly
        if (request.repeatCall === undefined) {
            request.repeatCall = false;
        }
        let p = this.getHttpParams(request);
        let headers = this.getHttpHeaders(request);
        httpObservable$ = this.http.get(this.getURL(request), { params: p, headers: headers, responseType: 'blob' });
        return this.finalizeRequest(request, httpObservable$); // Handles Repeat and wraps error handling
    }
    /**
     * Make a REST PUT Request, minimum needs an ApiResource and body (sometimes params can be sent in place of body but not standard).
     *
     * PUT requests typically used to add a single record or update a single record.
     *
     * By default if you don't include repeatCall in request parameter, this will set repeatCall to false and not repeat the call
     *
     * @param request AbstractRestParms interface, minimum required is ApiResource and body (params as non standard).
     *
     * @returns Observable, must subscribe in order for the call to process
     */
    put(request) {
        let httpObservable$;
        // Default to not repeat if not passed explicitly
        if (request.repeatCall === undefined) {
            request.repeatCall = false;
        }
        let headers = this.getHttpHeaders(request);
        let body;
        request.data ? body = request.data : body = null;
        httpObservable$ = this.http.put(this.getURL(request), body, { headers: headers });
        return this.finalizeRequest(request, httpObservable$); // Handles Repeat and wraps error handling
    }
    /**
     * Make a REST POST Request, minimum needs an ApiResource and body (sometimes params can be sent in place of body but not standard).
     *
     * PUT requests typically used to add a record or records, will typically not update records
     *
     * By default if you don't include repeatCall in request parameter, this will set repeatCall to false and not repeat the call
     *
     * @param request AbstractRestParms interface, minimum required is ApiResource and body (params as non standard). You will typically also
     * include repeatCall as false to not repeat this.
     *
     * @returns Observable, must subscribe in order for the call to process
     */
    post(request) {
        let httpObservable$;
        // Default to not repeat if not passed explicitly
        if (request.repeatCall === undefined) {
            request.repeatCall = false;
        }
        let headers = this.getHttpHeaders(request);
        let body;
        request.data ? body = request.data : body = null;
        httpObservable$ = this.http.post(this.getURL(request), body, { headers: headers });
        return this.finalizeRequest(request, httpObservable$); // Handles Repeat and wraps error handling
    }
    /**
     * Make a REST DELELTE Request, minimum needs an ApiResource and params.
     *
     * DELETE requests typically used to delete a singlel record or records.
     *
     * By default if you don't include repeatCall in request parameter, this will set repeatCall to false and not repeat the call
     *
     * @param request AbstractRestParms interface, minimum required is ApiResource and params. You will typically also
     * include repeatCall as false to not repeat this.
     *
     * @returns Observable, must subscribe in order for the call to process
     */
    delete(request) {
        let httpObservable$;
        // Default to not repeat if not passed explicitly
        if (request.repeatCall === undefined) {
            request.repeatCall = false;
        }
        let p = this.getHttpParams(request);
        let headers = this.getHttpHeaders(request);
        httpObservable$ = this.http.delete(this.getURL(request), { params: p, headers: headers });
        return this.finalizeRequest(request, httpObservable$); // Handles Repeat and wraps error handling
    }
    /**
     * Determines what the URL should be. Following rules determine the URL that will be returned in this order:
     *
     * If ApiCompleteURL is passed then that is return (ignored ApiResource, ApiHost, and ApiStage)
     *
     * If ApiResource is passed then the url returned will be apiGateway.url + apiGateway.stage + ApiResource (where apiGateway is injected from environment file in APIGATEWAY_CONFIG)
     *
     * If ApiResource and ApiStage is passed then the url returned will be apiGateway.url + ApiStage + ApiResource (where apiGateway is injected from environment file in APIGATEWAY_CONFIG)
     *
     * If ApiResource, ApiHost, and ApiStage is passed then the url returned will be ApiHost + ApiStage + ApiResource
     *
     * @param request AbstractRestParms interface, minimum required is ApiResource (with leading slash) or ApiCompleteURL, if ApiResource can optionally will include ApiHost and or ApiStage
     *
     * @returns The complete URL for the call to be made
     */
    getURL(request) {
        let url;
        if (request.ApiCompleteURL) {
            url = request.ApiCompleteURL;
        }
        else {
            let host;
            let stage;
            let resource;
            request.ApiHost ? host = request.ApiHost : host = this.apiGatewayConfig.url;
            request.ApiStage ? stage = request.ApiStage : stage = this.apiGatewayConfig.stage;
            request.ApiResource ? resource = request.ApiResource : resource = "/";
            url = host + stage + resource;
        }
        return url;
    }
    /**
     * Converts the dictionary params in request to a valild HttpParams object. This will add a parameter named "t" with the current date/time to help avoid browser caching by making each request unique
     *
     * @param request AbstractRestParms interface, typically params is included but not required
     *
     */
    getHttpParams(request) {
        let params;
        let disableTimeParam = false;
        request.data ? params = request.data : params = {};
        let p = new HttpParams({ fromObject: params });
        if (request.disableTimeParam !== undefined) {
            disableTimeParam = request.disableTimeParam;
        }
        if (!disableTimeParam) {
            p = p.append('t', `${Date.now()}`); // NOTE: HttpParams is immutablel, append() returns a cloned copy with new parameter added
        }
        return p;
    }
    /**
     * Determines which HttpHeaders should be added to the request based on the values in request. The following rules will be used:
     *
     * If ApiKey is included then return the following HttpHeaders:
     *    x-api-key = value of ApiKey
     *    DisableAuthHeader = true (this is used by the GeaMesAuthInterceptor to prevent it from adding the cognito Authorization header)
     *
     * If disableAuthHeader is ture then return the following HttpHeaders:
     *    DisableAuthHeader = true (this is used by the GeaMesAuthInterceptor to prevent it from adding the cognito Authorization header)
     *
     * @param request AbstractRestParms interface, no members required but ApiKey and disableAuthHeader are used if present
     *
     */
    getHttpHeaders(request) {
        let headers = new HttpHeaders();
        let useApiKey = false;
        let disableAuth = false;
        if (request.ApiKey !== undefined) {
            useApiKey = true;
        }
        if (request.disableAuthHeader !== undefined) {
            disableAuth = request.disableAuthHeader;
        }
        if (useApiKey) {
            let apiKey;
            request.ApiKey ? apiKey = request.ApiKey : apiKey = "";
            headers = headers.set("x-api-key", apiKey); // NOTE: HttpHeaders is immutable, set() returns a cloned copy with the new header added
            headers = headers.set('DisableAuthHeader', 'true');
        }
        else if (disableAuth) {
            headers = headers.set('DisableAuthHeader', 'true'); // NOTE: HttpHeaders is immutable, set() returns a cloned copy with the new header added
        }
        return headers;
    }
    /**
     * Wraps the final Observable in an error handler and optionally sets the request to repeat
     *
     * Note: If repeatCall is true and repeatJitter is true (defaults to true) will automatically calculate a random refresh rate within 84% of repeatRate or this.refreshTime.
     *
     * If retryAttempts is not set default to 3 attempts on errors
     *
     * If maxRetryWait is not set default to 10 minutes
     *
     * @param request AbstractRestParms interface, repeatCall required (defaults set in calling methods), optional repeatRate used if present otherwise use default
     * @param req The Observable for the HTTP request
     */
    finalizeRequest(request, req) {
        let retryAttempts = 3; // Default retry attempts to 3
        let maxRetryWait = 600000; // Default max retry time to 10 minutes (600 seconds)
        if (request.retryAttempts || request.retryAttempts == 0) {
            retryAttempts = request.retryAttempts;
        }
        if (request.maxRetryWait) {
            maxRetryWait = request.maxRetryWait;
        }
        if (request.repeatCall) {
            let repeatRate;
            let addJitter;
            request.repeatRate ? repeatRate = request.repeatRate : repeatRate = this.refreshTime;
            request.repeatJitter ? addJitter = request.repeatJitter : addJitter = true; // By default we will add jitter
            if (addJitter) {
                repeatRate = this.randomInt(repeatRate * 0.84, repeatRate);
            }
            console.info(`The request ${request.ApiResource} a repeat rate of ${repeatRate}.`);
            return this.wrapErrorHandler(timer(0, repeatRate).pipe(concatMap(_ => req)), retryAttempts, maxRetryWait);
        }
        else {
            return this.wrapErrorHandler(req, retryAttempts, maxRetryWait);
        }
    }
    /**
     * Error handler wrapper, determine what to do if the request fails using the following rules:
     *
     * @param req
     * @param retryAttempts Number of attempts to retry on failure
     * @param maxRetryWait Max time that any retry attempt should wait
     */
    wrapErrorHandler(req, retryAttempts, maxRetryWait) {
        return req.pipe(retryWhen(randomRetryStrategy({ excludedStatusCodes: this.retryExcludeCodes, maxRetryAttempts: retryAttempts, maxRetryWait: maxRetryWait })), catchError(err => this.handleError(err)));
    }
    /**
     * The handler if the request had a problem, use these rules for handling:
     *
     * If response code is 401 or 403 then reauthorize and then repeat the call after authenticated.
     * If response code 405 then do not repeat
     * If response code 500 or greater, call again but randomize the recall wait so it isn't immediately recalling
     * If response code 500 and after the configured retryAttempts, or 3 if not set, recall attempts, display error to user. Subscription will be lost at this point so need to consider what to do with that situation.
     *
     * @param err
     */
    handleError(err) {
        console.debug("restservice handleError", err);
        if (err.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', err.error.message);
        }
        else {
            console.error(`Backend returned code ${err.status}, ` +
                `body was: ${err.error}`);
        }
        return throwError(err);
    }
    /**
     * Returns true is the HttpErrorReponse is using a error status code that can be rerun
     *
     * @param err HttpErrorResponse received
     */
    isRerunnable(err) {
        if (this.retryExcludeCodes.find(e => e === err.status)) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * Returns a random integer. This is useful to use for providing jitter in timing and retry logic.
     *
     * @param min Minimum integer possible, inclusive
     * @param max Maximum integer possible, inclusive
     */
    randomInt(min, max) {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AbstractRestService, deps: [{ token: i1.HttpClient }, { token: APIGATEWAY_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AbstractRestService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AbstractRestService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [APIGATEWAY_CONFIG]
                }] }]; } });
/**
 * For use with rxjs operator retryWhen, a random retry strategy that will retry a random number of millliseconds before trying again. Each retry attempt has the potential of returning
 * a larger back off.
 *
 * Formula: retryAttemp * randomNumber between 0 and 1 * 10 * scalingDuration
 *
 */
const randomRetryStrategy = ({ maxRetryAttempts = 3, maxRetryWait = 600000, scalingDuration = 1000, excludedStatusCodes = [] } = {}) => (attempts) => {
    return attempts.pipe(mergeMap((error, i) => {
        const retryAttempt = i + 1;
        if (excludedStatusCodes.find(e => e === error.status)) {
            console.error("Call failed, status code excluded from retry attempts");
            return throwError(error); //ErrorObservable.create(error);
        }
        else if (retryAttempt > maxRetryAttempts && maxRetryAttempts >= 0) {
            console.error("Call failed, retry attempts exhausted.");
            return throwError(error); //ErrorObservable.create(error);
        }
        let retryTimer = retryAttempt * Math.random() * 10 * scalingDuration; // Can consider in the future changing this to randint(0,base*2**retryAttempt)
        if (retryTimer > maxRetryWait) {
            retryTimer = maxRetryWait - (Math.random() * 10); // Cap retry time, this is for when we end up at a very high retry attempt number. Adds a small amount of jitter.
        }
        console.error(`Attempt ${retryAttempt} of ${maxRetryAttempts}: retrying in ${retryTimer}ms`);
        return timer(retryTimer);
    }), finalize(() => console.error('Retry logic completed.')));
};

class GeaMesAdminService extends AbstractRestService {
    postInit() { }
    adminGroupList(application, role) {
        return this.get({
            ApiStage: "auth",
            ApiResource: "/getAdmin",
            data: {
                application: application,
                role: role
            },
            repeatCall: false
        });
    }
    acquireSession(computerName, apiKey) {
        let url = this.apiGatewayConfig.url;
        url = url.replace('-ha', ''); // Use non-HA domain if one was given to us
        return this.get({
            ApiHost: url,
            ApiStage: "auth",
            ApiResource: "/generateSession",
            ApiKey: apiKey,
            data: {
                ComputerName: computerName
            },
            repeatCall: false
        });
    }
    getBucketInfo() {
        let host = window.location.origin;
        let completeURL = `${host}/bucketinfo.json`;
        return this.get({
            ApiCompleteURL: completeURL,
            disableAuthHeader: true,
            repeatCall: false
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesAdminService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesAdminService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesAdminService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class GeaMesCognitoAuthService {
    constructor(document, baseHref, cognitoConfig, cognitoConfigHA, applicationName, allowScreenAuth, adminService, cookieService, toastr) {
        this.document = document;
        this.baseHref = baseHref;
        this.cognitoConfig = cognitoConfig;
        this.cognitoConfigHA = cognitoConfigHA;
        this.applicationName = applicationName;
        this.allowScreenAuth = allowScreenAuth;
        this.adminService = adminService;
        this.cookieService = cookieService;
        this.toastr = toastr;
        this.userpoolAvail$ = new BehaviorSubject(false);
        this.sessionEstablished$ = new ReplaySubject(1);
        this.sessionReady$ = new BehaviorSubject(false);
        this.idToken = '';
        if (this.cognitoConfig == null && this.cognitoConfigHA == null) {
            console.error("Missing Injectiong, must inject either COGNITO_CONFIG or COGNITO_CONFIG_HA!");
        }
    }
    /**
     * Executes the authentication process. If there is a cookie MES_AUTH_COMPUTERNAME then the ScreenAuth process will be used to load
     * a valid session. Otherwise Cognito will be triggered for login.
     */
    executeAuthProcess() {
        this.computerName = this.readCookie("MES_AUTH_COMPUTERNAME");
        if (this.allowScreenAuth) {
            this.screenAuth = true;
        }
        else {
            this.screenAuth = false;
        }
        if (this.computerName != "") {
            console.debug("ScreenAuth Configured: ", this.computerName);
            if (this.allowScreenAuth) {
                this.acquireSession();
            }
            else {
                console.error("This app cannot be used by a device using ScreenAuth.");
                this.toastr.error("This device is configured to autologon and this application is denied access via autologon. Please use another computer or use this browsers private browsing or incognito mode.", "Logon not allowed", {
                    disableTimeOut: true,
                    positionClass: "toast-center-center",
                    tapToDismiss: false
                });
            }
        }
        else {
            console.debug("Normal Logon configured, authenticate via Cognito");
            this.auth = this.getCognitoAuth();
        }
    }
    /**
     * If ScreenAuth is enabled, exchange the API Key for a valid cognito session.
     *
     */
    acquireSession() {
        console.debug("acaquireSession()", this.computerName);
        let apiKey = this.readCookie("MES_AUTH_APIKEY");
        let execute = false;
        // Check if we already acquiring a session, let that one finish
        if (this.session) {
            if (this.session.closed) {
                execute = true;
            }
        }
        else {
            execute = true;
        }
        if (execute) {
            console.debug("Call Generate Session");
            this.session = this.adminService.acquireSession(this.computerName, apiKey).subscribe(out => {
                console.debug("acquireSession returned a session.", out);
                this.cognitoEnvr.ClientId = out["Body"]["clientID"];
                let idToken = out["Body"]["CognitoSession"]["IdToken"];
                let accessToken = out["Body"]["CognitoSession"]["AccessToken"];
                let refreshToken = out["Body"]["CognitoSession"]["RefreshToken"];
                if (out["Body"]["api-key-rotate"]) {
                    console.debug("Rotate API Key");
                    this.setCookie("MES_AUTH_APIKEY", out["Body"]["api-key"]);
                    this.setCookie("MES_AUTH_COMPUTERNAME", this.computerName);
                }
                this.saveSession(idToken, accessToken, refreshToken);
                //if (initial) {
                this.auth = this.getCognitoAuth();
                //}
                this.auth.getSession();
                this.session.unsubscribe();
                console.debug(this.session.closed);
            });
        }
    }
    /**
     * Set a new cookie or update an existing cookie
     *
     * @param cookieName Cookie Name
     * @param cookieValue Cookie Value
     */
    setCookie(cookieName, cookieValue) {
        let host = window.location.hostname;
        host = host.slice(host.indexOf(".") + 1);
        this.cookieService.set(cookieName, cookieValue, 60, '/', host, true, 'Strict');
    }
    /**
     * Delete a cookie
     *
     * @param cookieName Cookie Name
     */
    deleteCookie(cookieName) {
        let host = window.location.hostname;
        host = host.slice(host.indexOf(".") + 1);
        this.cookieService.delete(cookieName, '/', host);
    }
    /**
     * Read a cookie
     *
     * @param cookieName Cookie Name
     */
    readCookie(cookieName) {
        return this.cookieService.get(cookieName);
    }
    /**
     * Used by acquireSession() to update local storage with the cognito session acquired by exchanging API Keys. This will be used by this.auth.setSession() to load the session
     * so it is used as normal
     *
     * @param idToken Unexpired idToken
     * @param accessToken Unexpired accessToken
     * @param refreshToken Unexpired refreshToken, typically blank for screenauth to alllow greater client control
     */
    saveSession(idToken, accessToken, refreshToken) {
        var lastUserName = "ScreenAuth";
        var keyPrefix = 'CognitoIdentityServiceProvider.' + this.cognitoEnvr.ClientId + '.' + lastUserName;
        var lastUserKey = 'CognitoIdentityServiceProvider.' + this.cognitoEnvr.ClientId + '.LastAuthUser';
        var idTokenKey = keyPrefix + '.idToken';
        var accessTokenKey = keyPrefix + '.accessToken';
        var refreshTokenKey = keyPrefix + '.refreshToken';
        var scopeKey = keyPrefix + '.tokenScopesString';
        localStorage.setItem(lastUserKey, lastUserName);
        localStorage.setItem(idTokenKey, idToken);
        localStorage.setItem(accessTokenKey, accessToken);
        localStorage.setItem(refreshTokenKey, refreshToken);
        localStorage.setItem(scopeKey, "openid profile email");
    }
    /**
     * Sets the status of the cognito session
     *
     * @param status Status of session
     */
    setSessionReady(status) {
        this.sessionReady$.next(status);
    }
    /**
     * Sets the status of the cognito userpool config.
     *
     * @param status Status of userpool
     */
    setUserpoolAvail$(status) {
        this.userpoolAvail$.next(status);
    }
    getCognitoAuth(logOutMode = '') {
        let myopts = this.cognitoEnvr;
        let userHandler = {
            onSuccess: function (sess) {
                console.debug("Auth Success");
                this.sessionEstablished$.next(sess);
                this.idToken = sess.getIdToken().getJwtToken(); // Store the token
                this.setSessionReady(true); // Used to signal downstream that session is ready
                console.debug("Session", sess);
            }.bind(this),
            onFailure: function (err) {
                console.error("auth failure", err);
                console.error("gonna sign out via redirect", this.auth.getFQDNSignOut());
                this.sessionEstablished$.next(undefined);
                this.idToken = "";
                this.setSessionReady(false); // Used to signal downstream that session is ready        
                this.auth.signOut();
            }.bind(this)
        };
        myopts['LaunchUri'] = (url) => {
            console.debug("REDIRECT INTERCEPT", url);
            this.document.location.href = url;
        };
        myopts.RedirectUriSignIn = window.location.origin + this.baseHref + 'login';
        myopts.RedirectUriSignOut = window.location.origin + this.baseHref + logOutMode;
        console.debug('redirect url is', myopts.RedirectUriSignIn);
        console.debug('log out is:', myopts.RedirectUriSignOut);
        let auth = new CognitoAuth(myopts);
        auth.setState(window.location.toString().replace("/logout", ""));
        console.debug(auth);
        auth.useCodeGrantFlow();
        auth.userhandler = userHandler;
        return auth;
    }
    validateSession() {
        if (this.auth == null) {
            this.executeAuthProcess();
        }
        if (this.computerName != "" && this.screenAuth) {
            this.acquireSession();
        }
        else {
            this.auth.getSession();
            console.debug("Validate Session Called");
        }
    }
    /**
     * If user is logged in returns a BehaviorSubject with a value of true to signal user is logged in.
     * If user session is expired triggers session refresh and returns a BehaviorSubject witha  value of false, can be subscribed to wait for true to indicate
     * the user session is ready
     */
    isUserSignedIn() {
        console.debug("Check if user is signedin");
        if (this.auth != null && this.sessionReady$.getValue() && this.auth.isUserSignedIn()) {
            console.debug("User signed in.");
        }
        else {
            console.debug("User not signed in.");
            this.setSessionReady(false);
            this.getCognitoEnvironment().pipe(filter(val => val === true), take(1)).subscribe(() => {
                console.debug("UserpoolAvail$ now true, complete isUserSignedIn()");
                if (this.auth == null) {
                    this.executeAuthProcess();
                }
                if (this.sessionReady$.getValue() && this.auth.isUserSignedIn()) {
                    console.debug("User already signed in");
                }
                else {
                    console.debug("User NOT signed in.");
                    this.setSessionReady(false);
                    this.validateSession();
                }
            });
        }
        return this.sessionReady$;
    }
    handleLoginPromise(queryString) {
        this.getCognitoEnvironment().pipe(filter(val => val === true), take(1)).subscribe(() => {
            console.debug("UserpoolAvail$ now true, complete handleLoginPromise()");
            if (this.auth == null) {
                this.executeAuthProcess();
            }
            if (!this.auth.isUserSignedIn() && queryString.match(/[token|code]=/)) {
                console.debug("parsing oauth response");
                this.auth.parseCognitoWebResponse(queryString);
            }
            else
                this.validateSession();
        });
        return this.sessionEstablished$.pipe(take(1)).toPromise();
    }
    isUserInRole(role = "Admin") {
        console.debug("Checking if user is in role ", role);
        if (this.auth == null) {
            this.executeAuthProcess();
        }
        if (this.groupList == undefined) {
            this.groupList = {};
        }
        if (!(role in this.groupList)) {
            console.debug("First time seeing role");
            return new Observable((observer) => {
                this.adminService.adminGroupList(this.applicationName, role).subscribe(out => {
                    console.debug(this.groupList);
                    this.groupList[role] = out["Body"]["Groups" + role];
                    observer.next(this.checkUserRole(role));
                    observer.complete();
                });
            });
        }
        else {
            console.debug("Role already loaded");
            return of(this.checkUserRole(role));
        }
    }
    checkUserRole(role) {
        let roleGroupList = new Set(this.groupList[role]);
        if ('cognito:groups' in this.auth['signInUserSession']['idToken']['payload']) {
            this.userGroupList = this.auth['signInUserSession']['idToken']['payload']['cognito:groups'];
        }
        else {
            this.userGroupList = [];
        }
        console.debug(this.userGroupList, roleGroupList);
        return this.userGroupList.some(group => {
            return roleGroupList.has(group);
        });
    }
    getUserAttribute(attribute) {
        console.debug("getCognitoUserAttribute", attribute);
        if (this.auth == null) {
            this.executeAuthProcess();
        }
        const allowAttributeMap = {
            "sso": "preferred_username",
            "firstName": "given_name",
            "lastName": "family_name",
            "email": "email",
            "userpoolid": "sub",
            "trackingid": "sub"
        };
        let output = "";
        if (attribute in allowAttributeMap) {
            let tokenAttrib = allowAttributeMap[attribute];
            if (attribute == "trackingid") {
                console.debug("ComputerName", this.computerName);
                if (this.computerName == "") {
                    output = this.auth['signInUserSession']['idToken']['payload'][tokenAttrib];
                }
                else {
                    output = this.computerName;
                }
            }
            else if (tokenAttrib in this.auth['signInUserSession']['idToken']['payload']) {
                output = this.auth['signInUserSession']['idToken']['payload'][tokenAttrib];
            }
            else if (attribute == "sso" && "cognito:username" in this.auth['signInUserSession']['idToken']['payload']) {
                // If looking for SSO and preferred_username doesn't exist, then use cognito:username
                output = this.auth['signInUserSession']['idToken']['payload']['cognito:username'];
            }
        }
        else {
            output = "ATTRIBUTE NOT WHITELISTED";
        }
        return output;
    }
    signOutUser() {
        this.auth = this.getCognitoAuth('logout');
        this.auth.signOut();
    }
    /**
     * Retrieve the correct cognito environment. Will use the following:
     */
    getCognitoEnvironment() {
        console.debug("Retrieve Userpool config");
        console.debug("COGNITO_CONFIG", this.cognitoConfig);
        console.debug("COGNITO_CONFIG_HA", this.cognitoConfigHA);
        if (this.cognitoConfigHA == null && this.cognitoConfig != null) {
            console.debug("Using standard cognito config");
            this.cognitoEnvr = this.cognitoConfig;
            this.setUserpoolAvail$(true);
        }
        else if (this.cognitoConfigHA != null) {
            console.debug("Using HA cognito config.");
            this.setUserpoolAvail$(false);
            this.adminService.getBucketInfo().pipe(take(1)).subscribe((info) => {
                console.debug("BucketInfo", info);
                let cognitoRegion = info.COGNITOREGION;
                let myCognitoConfig = this.cognitoConfigHA.find(x => x['Region'] == cognitoRegion);
                if (myCognitoConfig != null) {
                    this.cognitoEnvr = myCognitoConfig;
                    if (this.auth != null) {
                        if (this.auth.getAppWebDomain() != this.cognitoEnvr.AppWebDomain) {
                            console.debug("UserPool changed", this.auth.getAppWebDomain(), this.cognitoEnvr.AppWebDomain);
                            this.auth.clearCachedTokensScopes(); // Clear cached token scopes
                            this.auth = this.getCognitoAuth();
                        }
                        else {
                            console.debug("No change in UserPool");
                        }
                    }
                    this.setUserpoolAvail$(true);
                }
                else {
                    console.error("Cognito Configuration not found for region", cognitoRegion);
                }
            }, (err) => {
                console.error("Failed to retrieve bucketinfo.json", err);
            });
        }
        else {
            console.error("Missing Cognito Config");
        }
        return this.userpoolAvail$;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAuthService, deps: [{ token: DOCUMENT }, { token: APP_BASE_HREF }, { token: COGNITO_CONFIG, optional: true }, { token: COGNITO_CONFIG_HA, optional: true }, { token: APPLICATION_NAME, optional: true }, { token: ALLOW_SCREENAUTH, optional: true }, { token: GeaMesAdminService }, { token: i2.CookieService }, { token: i1$1.ToastrService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_BASE_HREF]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [COGNITO_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [COGNITO_CONFIG_HA]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [APPLICATION_NAME]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ALLOW_SCREENAUTH]
                }] }, { type: GeaMesAdminService }, { type: i2.CookieService }, { type: i1$1.ToastrService }]; } });

class GeaMesLoginComponent {
    constructor(route, router, service, locStrategy) {
        this.route = route;
        this.router = router;
        this.service = service;
        this.locStrategy = locStrategy;
    }
    ngOnInit() {
        let fullpath = this.locStrategy.path(true).replace('/login', '');
        this.service.handleLoginPromise(fullpath)
            .then(_ => {
            console.debug("valid session rerouting to protected content");
            let myLocation = window.location.origin + window.location.pathname;
            this.route.queryParams.subscribe((params) => {
                console.debug("Query Params", params);
                if ("state" in params) {
                    let redirectURL = params["state"];
                    let redirectLocation = redirectURL.substring(0, redirectURL.indexOf("?"));
                    console.debug("State Location and my location", redirectLocation, myLocation);
                    if (redirectLocation != myLocation) {
                        console.debug("Redirect to", redirectURL);
                        window.location.replace(redirectURL);
                    }
                    else {
                        console.debug("Avoid redirect loop, no redirect");
                        this.router.navigate(['/']);
                    }
                }
                else {
                    console.debug("No state found, go to default");
                    this.router.navigate(['/']);
                }
            });
        }, (rej) => {
            console.error("rejected", rej);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesLoginComponent, deps: [{ token: i1$2.ActivatedRoute }, { token: i1$2.Router }, { token: GeaMesCognitoAuthService }, { token: i3.LocationStrategy }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: GeaMesLoginComponent, selector: "gea-mes-login", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesLoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gea-mes-login', template: '' }]
        }], ctorParameters: function () { return [{ type: i1$2.ActivatedRoute }, { type: i1$2.Router }, { type: GeaMesCognitoAuthService }, { type: i3.LocationStrategy }]; } });

function getBaseHref(platformLocation) {
    return platformLocation.getBaseHrefFromDOM();
}

class GeaMesCognitoModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoModule, declarations: [GeaMesLoginComponent], imports: [i1$1.ToastrModule], exports: [GeaMesLoginComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoModule, providers: [
            { provide: APP_BASE_HREF, useFactory: getBaseHref, deps: [PlatformLocation] },
            GeaMesCognitoAuthService,
            CookieService
        ], imports: [ToastrModule.forRoot()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        GeaMesLoginComponent,
                    ],
                    imports: [
                        ToastrModule.forRoot()
                    ],
                    providers: [
                        { provide: APP_BASE_HREF, useFactory: getBaseHref, deps: [PlatformLocation] },
                        GeaMesCognitoAuthService,
                        CookieService
                    ],
                    exports: [
                        GeaMesLoginComponent
                    ]
                }]
        }] });

class GeaMesCognitoAuthGuard {
    constructor(service) {
        this.service = service;
    }
    canActivate(_, __) {
        console.debug('Auth Guard canActivate');
        return this.service.isUserSignedIn().pipe(filter(val => val === true), take(1));
    }
    canActivateChild(next, state) {
        return this.canActivate(next, state);
    }
    canLoad(route, segments) {
        return this.canActivate(null, null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAuthGuard, deps: [{ token: GeaMesCognitoAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAuthGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAuthGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: GeaMesCognitoAuthService }]; } });

class GeaMesCognitoAdminGuard {
    constructor(service) {
        this.service = service;
    }
    canActivate(_, __) {
        return this.service.isUserInRole();
    }
    canActivateChild(next, state) {
        return this.canActivate(next, state);
    }
    canLoad(route, segments) {
        return this.canActivate(null, null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAdminGuard, deps: [{ token: GeaMesCognitoAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAdminGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesCognitoAdminGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: GeaMesCognitoAuthService }]; } });

class GeaMesErrorHandlingService {
    constructor(toastr, apiGatewayConfig) {
        this.toastr = toastr;
        this.apiGatewayConfig = apiGatewayConfig;
        this.errorSet = {};
        this.autoClear = this.apiGatewayConfig.clearErrors ? true : false;
    }
    /**
     * Genereates a unique call signature for the HTTP call to track it from error to success.
     *
     * @param callUrl URL of the REST call
     * @param callKey A unique value to appeand
     * @returns
     */
    formCallSigniture(callUrl, callKey = "") {
        let callSigniture = '';
        let urlSub = '';
        if (callUrl.includes("?")) {
            let iLastChar = callUrl.indexOf("?");
            let ifirstChar = callUrl.substring(0, iLastChar).lastIndexOf("/") + 1;
            urlSub = callUrl.substring(ifirstChar, iLastChar);
        }
        else {
            let ifirstChar = callUrl.lastIndexOf("/") + 1;
            urlSub = callUrl.substring(ifirstChar);
        }
        callSigniture = urlSub + callKey;
        return callSigniture;
    }
    /**
     * Handle an error call, triggers a toastr message
     *
     * @param error HttpErrorRepsonse for the failed call
     * @param method The HTTP Method (GET/PUT/POST/DELETE)
     */
    addToErrors(error, method) {
        let url = error.url != null ? error.url : '';
        let callSigniture = this.formCallSigniture(url, method);
        let existingError = this.errorSet.hasOwnProperty(callSigniture);
        if (error.status != 0) {
            if (!existingError) {
                let toastrRef = this.toastr.error(error['error']['stackTrace'][0], error['error']['errorMessage'], {
                    disableTimeOut: true,
                    positionClass: "toast-bottom-full-width",
                    tapToDismiss: false
                });
                this.errorSet[callSigniture] = toastrRef.toastId;
            }
        }
    }
    /**
     * Handle a successful call, if APIGATEWAY_CONFIG injected config has clearerror set to true then clear any previous errors
     * for the given resource.
     *
     * @param call The HttPRequest of the successfull call
     */
    sucessCall(call) {
        if (this.autoClear) {
            let url = call.url != null ? call.url : '';
            let callSigniture = this.formCallSigniture(url, call.method);
            let existingError = this.errorSet.hasOwnProperty(callSigniture);
            if (existingError) {
                this.toastr.clear(this.errorSet[callSigniture]);
                delete this.errorSet[callSigniture];
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesErrorHandlingService, deps: [{ token: i1$1.ToastrService }, { token: APIGATEWAY_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesErrorHandlingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesErrorHandlingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.ToastrService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [APIGATEWAY_CONFIG]
                }] }]; } });

/** Pass untouched request through to the next request handler. */
class GeaMesAuthInterceptor {
    constructor(authService, errorService) {
        this.authService = authService;
        this.errorService = errorService;
    }
    intercept(req, next) {
        if (req.headers.get("DisableAuthHeader") == "true") {
            console.debug("Send without Authorization header");
            return this.generateHttpReq(req, next);
        }
        else {
            return this.authService.isUserSignedIn().pipe(filter(val => val === true), // Only take a session ready as true otherwise wait
            take(1), switchMap(ready => {
                console.debug("Session returned, can now pass on to next steps", ready);
                let thisReq;
                thisReq = req.clone({ setHeaders: { 'Authorization': this.authService.idToken } });
                return this.generateHttpReq(thisReq, next);
            }));
        }
    }
    generateHttpReq(req, next) {
        return next.handle(req).pipe(map(evt => {
            if (evt instanceof HttpResponse) {
                this.errorService.sucessCall(req); // Clear any prior errors from this resource
            }
            return evt;
        }), catchError(err => {
            if (err instanceof HttpErrorResponse && (err.status == 401)) {
                console.debug("intercept revalidating session on error");
                this.authService.validateSession();
                return throwError(err);
            }
            else if (req.responseType == 'blob' && err.error instanceof Blob && err.error.type == 'application/json') {
                // Need to convert the blob to text and parse the JSON so it can be processed by the error handler and application
                return from(Promise.resolve(err).then(async (x) => {
                    let myErr = new HttpErrorResponse({
                        error: JSON.parse(await x.error.text()),
                        headers: err.headers,
                        status: err.status,
                        statusText: err.statusText,
                        url: err.url || undefined,
                    });
                    this.errorService.addToErrors(myErr, req.method); // Add error to toastr
                    throw myErr;
                }));
            }
            else {
                this.errorService.addToErrors(err, req.method); // Add error to toastr
            }
            return throwError(err);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesAuthInterceptor, deps: [{ token: GeaMesCognitoAuthService }, { token: GeaMesErrorHandlingService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesAuthInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GeaMesAuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: GeaMesCognitoAuthService }, { type: GeaMesErrorHandlingService }]; } });

/*
 * Public API Surface of gea-mes-cognito
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ALLOW_SCREENAUTH, APIGATEWAY_CONFIG, APPLICATION_NAME, AbstractRestService, COGNITO_CONFIG, COGNITO_CONFIG_HA, GeaMesAuthInterceptor, GeaMesCognitoAdminGuard, GeaMesCognitoAuthGuard, GeaMesCognitoAuthService, GeaMesCognitoModule, GeaMesErrorHandlingService, GeaMesLoginComponent, GenericLambdaReturn, getBaseHref };
